package com.castly.shared.dto

import kotlinx.serialization.Serializable

@Serializable
data class ImportReportDto(
    val importId: Int,
    val importType: String,  // CSV_AUDIOBOOK or FOLDER_EPISODE
    val status: String,  // SUCCESS, PARTIAL, FAILED
    val totalRows: Int,
    val successfulRows: Int,
    val failedRows: Int,
    val errors: List<ImportErrorDto>,
    val createdAt: String
)

@Serializable
data class ImportErrorDto(
    val row: Int? = null,
    val filename: String? = null,
    val error: String
)

@Serializable
data class ImportProgressDto(
    val currentRow: Int,
    val totalRows: Int,
    val successCount: Int,
    val failureCount: Int,
    val currentFile: String? = null
)

@Serializable
data class CsvImportRequest(
    val validateOnly: Boolean = false
)

@Serializable
data class EpisodeImportRequest(
    val overwriteDuplicates: Boolean = false
)

@Serializable
data class DuplicateEpisodePrompt(
    val episodeNumber: Int,
    val existingFile: String,
    val newFile: String,
    val existingDuration: Int?,
    val newDuration: Int?
)

@Serializable
data class DuplicateEpisodeResolution(
    val episodeNumber: Int,
    val action: String  // "skip", "overwrite", "rename"
)

@Serializable
data class SyncReportDto(
    val scannedCount: Int,
    val syncedCount: Int,
    val skippedCount: Int = 0,
    val errors: List<String>
)

@Serializable
data class ImportLogSummaryDto(
    val id: Int,
    val importType: String,
    val status: String,
    val totalRows: Int,
    val successfulRows: Int,
    val failedRows: Int,
    val createdAt: String,
    val source: String? = null,
    val contentType: String? = null,
    val entityId: String? = null,
    val entityName: String? = null
)

@Serializable
data class ImportLogDetailDto(
    val id: Int,
    val importType: String,
    val status: String,
    val totalRows: Int,
    val successfulRows: Int,
    val failedRows: Int,
    val errorReport: String? = null,
    val createdAt: String,
    val source: String? = null,
    val contentType: String? = null,
    val entityId: String? = null,
    val entityName: String? = null
)

@Serializable
data class ImportHistoryResponse(
    val items: List<ImportLogSummaryDto>,
    val total: Int
)

// ── CSV Preview DTOs ────────────────────────────────────────────

@Serializable
data class CsvPreviewResponse(
    val detectedEncoding: String,
    val totalRows: Int,
    val previewRows: List<CsvPreviewRow>,
    val detectedHeaders: List<String>,
    val columnMapping: List<ColumnMappingStatus>,
    val duplicateCheck: DuplicateCheckSummary,
    val summary: CsvImportSummary,
    val parseErrors: List<ImportErrorDto>
)

@Serializable
data class CsvPreviewRow(
    val rowNumber: Int,
    val isbn: String,
    val title: String,
    val author: String,
    val subtitle: String? = null,
    val publisher: String? = null,
    val language: String? = null,
    val status: String  // "new", "exists", "duplicate_in_file", "parse_error"
)

@Serializable
data class ColumnMappingStatus(
    val expectedColumn: String,
    val isRequired: Boolean,
    val detected: Boolean,
    val detectedAs: String? = null
)

@Serializable
data class DuplicateCheckSummary(
    val existingCount: Int,
    val newCount: Int,
    val duplicateWithinFileCount: Int,
    val existingIsbns: List<String>
)

@Serializable
data class CsvImportSummary(
    val willCreate: Int,
    val willSkip: Int,
    val willFail: Int
)
